/**
 * Pending account-link state and response parsing for Cognito provider linking.
 * The pending social ID token stays here, not in the signed-in session.
 */

import { decodeJWT } from '@/utils/tokenUtils';

const STORAGE_KEY = 'gt_account_link';

export type AuthMethodProvider = 'email' | 'google' | 'apple';

/** `GET /api/v2/auth/methods`. `email` is null when the backend predates it. */
export interface AuthMethodsSummary {
  methods: AuthMethodProvider[];
  email: string | null;
}

/** Where the collision prompt was triggered (for analytics). */
export type AccountLinkPromptEntry = 'oauth_callback' | 'email_sync';

export type StoredAccountLink =
  | {
      kind: 'collision';
      pendingIdToken: string;
      existingMethods: AuthMethodProvider[];
      redirectPath: string | null;
      deferOnboarding: boolean;
      entry?: AccountLinkPromptEntry;
    }
  | {
      kind: 'prove';
      pendingIdToken: string;
      existingMethods: AuthMethodProvider[];
      proveProvider: 'google' | 'apple';
      redirectPath: string | null;
      deferOnboarding: boolean;
    }
  | {
      kind: 'settings';
      provider: 'google' | 'apple';
      destinationAccessToken: string;
      destinationIdToken: string;
      destinationRefreshToken: string | null;
      /** OAuth return path if Cognito `state` is missing or truncated. */
      redirectPath?: string | null;
    }
  | {
      kind: 'reauth';
      provider: 'google' | 'apple';
      redirectPath: string | null;
      deferOnboarding: boolean;
      origin?: 'settings_link' | 'collision_prove';
    };

export class AccountLinkRequiredError extends Error {
  readonly code = 'ACCOUNT_LINK_REQUIRED' as const;
  readonly existingMethods: AuthMethodProvider[];

  constructor(existingMethods: AuthMethodProvider[]) {
    super('An account with this email already exists');
    this.name = 'AccountLinkRequiredError';
    this.existingMethods = existingMethods;
  }
}

export class EmailUnverifiedError extends Error {
  readonly code = 'EMAIL_UNVERIFIED' as const;

  constructor() {
    super('Email address is not verified');
    this.name = 'EmailUnverifiedError';
  }
}

export class AuthMethodChangeError extends Error {
  readonly code: 'LAST_METHOD' | 'PASSWORD_REQUIRED';

  constructor(code: 'LAST_METHOD' | 'PASSWORD_REQUIRED') {
    super(code === 'LAST_METHOD'
      ? 'Keep at least one way to sign in'
      : 'Set a password before disconnecting this sign-in method');
    this.name = 'AuthMethodChangeError';
    this.code = code;
  }
}

export type AuthPasswordErrorCode = 'PASSWORD_POLICY' | 'ALREADY_HAS_PASSWORD';

export class AuthPasswordError extends Error {
  readonly code: AuthPasswordErrorCode;

  constructor(code: AuthPasswordErrorCode) {
    super(code === 'PASSWORD_POLICY'
      ? 'Password does not meet the requirements'
      : 'This account already has a password');
    this.name = 'AuthPasswordError';
    this.code = code;
  }
}

export function isAuthPasswordError(err: unknown): err is AuthPasswordError {
  return err instanceof AuthPasswordError;
}

export function isAccountLinkRequiredError(err: unknown): err is AccountLinkRequiredError {
  return err instanceof AccountLinkRequiredError;
}

export function isEmailUnverifiedError(err: unknown): err is EmailUnverifiedError {
  return err instanceof EmailUnverifiedError;
}

export function isAuthMethodChangeError(err: unknown): err is AuthMethodChangeError {
  return err instanceof AuthMethodChangeError;
}

export function isAuthMethodProvider(value: unknown): value is AuthMethodProvider {
  return value === 'email' || value === 'google' || value === 'apple';
}

export function parseAuthMethodList(value: unknown): AuthMethodProvider[] {
  if (!Array.isArray(value)) return [];
  const methods: AuthMethodProvider[] = [];
  for (const item of value) {
    const provider = typeof item === 'string'
      ? item
      : (item && typeof item === 'object' && 'provider' in item
        ? (item as { provider?: unknown }).provider
        : null);
    if (!isAuthMethodProvider(provider) || methods.includes(provider)) continue;
    methods.push(provider);
  }
  return methods;
}

export function parseAuthMethodsResponse(body: unknown): AuthMethodsSummary {
  if (!body || typeof body !== 'object') return { methods: [], email: null };
  const { methods, email } = body as { methods?: unknown; email?: unknown };
  return {
    methods: parseAuthMethodList(methods),
    email: typeof email === 'string' && email.trim() ? email.trim() : null,
  };
}

export function classifyAuthFailure(status: number | undefined, data: unknown):
  | AccountLinkRequiredError
  | EmailUnverifiedError
  | AuthMethodChangeError
  | AuthPasswordError
  | null {
  if (!data || typeof data !== 'object' || !('code' in data)) return null;
  const code = (data as { code?: unknown }).code;
  if (status === 409 && code === 'ACCOUNT_LINK_REQUIRED') {
    const methods = parseAuthMethodList((data as { existing_methods?: unknown }).existing_methods);
    return new AccountLinkRequiredError(methods);
  }
  if (status === 400 && code === 'EMAIL_UNVERIFIED') {
    return new EmailUnverifiedError();
  }
  if (status === 409 && (code === 'LAST_METHOD' || code === 'PASSWORD_REQUIRED')) {
    return new AuthMethodChangeError(code);
  }
  if (status === 400 && (code === 'PASSWORD_POLICY' || code === 'ALREADY_HAS_PASSWORD')) {
    return new AuthPasswordError(code);
  }
  return null;
}

export function readReauthRequired(data: unknown): boolean {
  return Boolean(data && typeof data === 'object' && (data as { reauth_required?: unknown }).reauth_required === true);
}

function canUseSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

export function saveAccountLink(value: StoredAccountLink): void {
  if (!canUseSessionStorage()) return;
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

export function readAccountLink(): StoredAccountLink | null {
  if (!canUseSessionStorage()) return null;
  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredAccountLink;
    if (!parsed || typeof parsed !== 'object' || !('kind' in parsed)) return null;
    if (parsed.kind === 'collision' || parsed.kind === 'prove' || parsed.kind === 'settings' || parsed.kind === 'reauth') {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

export function clearAccountLink(): void {
  if (!canUseSessionStorage()) return;
  window.sessionStorage.removeItem(STORAGE_KEY);
}

export function writeAuthSession(tokens: {
  accessToken: string;
  idToken: string;
  refreshToken?: string | null;
}): void {
  localStorage.setItem('accessToken', tokens.accessToken);
  localStorage.setItem('idToken', tokens.idToken);
  if (tokens.refreshToken) {
    localStorage.setItem('refreshToken', tokens.refreshToken);
  }
  localStorage.setItem('isLogin', 'true');
}

export function clearAuthSession(): void {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('idToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('isLogin');
}

export function readAuthSession(): {
  accessToken: string | null;
  idToken: string | null;
  refreshToken: string | null;
} {
  return {
    accessToken: localStorage.getItem('accessToken'),
    idToken: localStorage.getItem('idToken'),
    refreshToken: localStorage.getItem('refreshToken'),
  };
}

export function holdCollisionAndClearSession(
  existingMethods: AuthMethodProvider[],
  redirectPath: string | null,
  deferOnboarding: boolean,
): boolean {
  const idToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
  if (!idToken) return false;
  clearAuthSession();
  saveAccountLink({
    kind: 'collision',
    pendingIdToken: idToken,
    existingMethods,
    redirectPath,
    deferOnboarding,
    entry: 'email_sync',
  });
  return true;
}

export function providerLabel(provider: AuthMethodProvider): string {
  if (provider === 'google') return 'Google';
  if (provider === 'apple') return 'Apple';
  return 'Email';
}

export function oauthProviderName(provider: 'google' | 'apple'): 'Google' | 'Apple' {
  return provider === 'google' ? 'Google' : 'Apple';
}

/** Best-effort read of which social provider the pending collision token came from. */
export function inferPendingOAuthProvider(idToken: string): 'google' | 'apple' | null {
  let decoded;
  try {
    decoded = decodeJWT(idToken);
  } catch {
    return null;
  }
  if (!decoded) return null;

  const usernameRaw =
    (typeof decoded['cognito:username'] === 'string' && decoded['cognito:username'])
    || (typeof decoded.sub === 'string' && decoded.sub)
    || '';
  const username = usernameRaw.toLowerCase();
  if (username.startsWith('google_')) return 'google';
  if (username.startsWith('signinwithapple_')) return 'apple';

  const identities = decoded.identities;
  if (Array.isArray(identities)) {
    for (const entry of identities) {
      if (!entry || typeof entry !== 'object' || !('providerName' in entry)) continue;
      const name = String((entry as { providerName?: unknown }).providerName).toLowerCase();
      if (name === 'google') return 'google';
      if (name === 'signinwithapple' || name === 'apple') return 'apple';
    }
  }

  return null;
}

